import type { Message } from "@/types/chat";
import { MessageView } from "@/components/message/MessageView";
import { useRef, useCallback, useLayoutEffect } from "react";

interface ChatProps {
  id: string;
  messages: Message[];
  streamingMessageId?: string;
  onLoadMore?: () => void;
}

export function ChatView({
  id,
  messages,
  streamingMessageId,
  onLoadMore,
}: ChatProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const followNewMessageRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const loadMoreTriggeredRef = useRef(false);
  const scrollStateRef = useRef({
    scrollHeight: 0,
    messageCount: 0,
    firstMessageId: undefined as string | undefined,
  });

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < 30;
    isAtBottomRef.current = atBottom;

    // 仅在用户主动向上滚动时取消跟随，避免流式输出撑高内容后误判
    if (el.scrollTop < lastScrollTopRef.current - 10) {
      followNewMessageRef.current = false;
    }
    lastScrollTopRef.current = el.scrollTop;

    if (el.scrollTop < 50) {
      if (!loadMoreTriggeredRef.current) {
        loadMoreTriggeredRef.current = true;
        onLoadMore?.();
      }
    } else if (el.scrollTop > 150) {
      loadMoreTriggeredRef.current = false;
    }
  }, [onLoadMore]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const prev = scrollStateRef.current;
    const prepended =
      messages.length > prev.messageCount &&
      messages[0]?.id !== prev.firstMessageId;
    const appendedAtEnd =
      messages.length > prev.messageCount &&
      messages[0]?.id === prev.firstMessageId;

    if (prepended) {
      const heightDiff = el.scrollHeight - prev.scrollHeight;
      el.scrollTop += heightDiff;
    } else {
      if (appendedAtEnd) {
        const newMessages = messages.slice(prev.messageCount);
        if (newMessages.some((message) => message.role === "user")) {
          followNewMessageRef.current = true;
        }
      }

      const shouldScroll =
        (prev.messageCount === 0 && messages.length > 0) ||
        isAtBottomRef.current ||
        followNewMessageRef.current;

      if (shouldScroll) {
        el.scrollTop = el.scrollHeight;
        if (followNewMessageRef.current) {
          isAtBottomRef.current = true;
        }
      }
    }

    scrollStateRef.current = {
      scrollHeight: el.scrollHeight,
      messageCount: messages.length,
      firstMessageId: messages[0]?.id,
    };
  }, [messages]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="h-full w-full max-w-full overflow-y-auto no-scrollbar"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <div className="h-4 shrink-0" />
      {messages.map((message, index) => (
        <div
          key={message.id}
          className="w-full flex flex-col items-center px-4 pb-4 min-h-[1rem]"
        >
          <MessageView
            chatId={id}
            role={message.role}
            content={message.content}
            attachments={message.files}
            toolInvocations={message.toolInvocations}
            isStreaming={message.id === streamingMessageId}
            streamId={message.streamId}
            quote={message.quote}
            previousMessage={messages[index - 1]}
          />
        </div>
      ))}
      <div className="h-4 shrink-0" />
    </div>
  );
}
